/**
 * Star Ratings Service
 * Manages Aged Care Star Ratings calculation and tracking
 */

import LocalStorageService from '../storage/LocalStorageService';
import {
  createStarRating,
  calculateOverallRating,
  calculateResidentsExperienceScore,
  calculateComplianceScore,
  calculateStaffingScore,
  calculateQualityMeasuresScore
} from '../../data/models/StarRatings';
import CareMinutesService from '../care/CareMinutesService';
import { starRatingsData } from '../../data/mockData';

class StarRatingsService {
  constructor() {
    this.storageKey = 'starRatings';
    this.initializeMockData();
  }

  /**
   * Initialize mock data if storage is empty
   */
  initializeMockData() {
    try {
      const existing = LocalStorageService.getAll(this.storageKey);
      if (existing.length === 0 && starRatingsData) {
        starRatingsData.forEach(sr => {
          LocalStorageService.addItem(this.storageKey, sr);
        });
      }
    } catch (error) {
      console.error('Error initializing star ratings mock data:', error);
    }
  }

  /**
   * Get current star rating for a facility
   */
  async getCurrentRating(facilityId) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      const ratings = all
        .filter(sr => sr.facilityId === facilityId)
        .sort((a, b) => new Date(b.assessmentDate) - new Date(a.assessmentDate));
      
      if (ratings.length > 0) {
        return { success: true, data: ratings[0] };
      }
      
      return { success: false, error: 'No star rating found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all star ratings for a facility (historical)
   */
  async getRatingHistory(facilityId) {
    try {
      const all = LocalStorageService.getAll(this.storageKey);
      const ratings = all
        .filter(sr => sr.facilityId === facilityId)
        .sort((a, b) => new Date(b.assessmentDate) - new Date(a.assessmentDate));
      
      return { success: true, data: ratings };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate and save star rating
   */
  async calculateStarRating(facilityId, componentData = {}) {
    try {
      // Get current date
      const today = new Date().toISOString().split('T')[0];
      
      // Calculate component scores
      const residentsExperienceScore = calculateResidentsExperienceScore(
        componentData.consumerSurvey,
        componentData.familyFeedback,
        componentData.satisfactionMetrics
      );
      
      const complianceScore = calculateComplianceScore(
        componentData.qualityStandards,
        componentData.auditResults,
        componentData.sirsCompliance
      );
      
      // Get care minutes compliance for staffing component
      const careMinutesSummary = await CareMinutesService.getFacilityComplianceSummary(facilityId, today);
      const careMinutesCompliant = careMinutesSummary.success && 
        careMinutesSummary.data.complianceRate >= 95;
      const rnCompliant = careMinutesSummary.success && 
        careMinutesSummary.data.rnCompliantResidents === careMinutesSummary.data.totalResidents;
      
      const staffingScore = calculateStaffingScore(
        careMinutesCompliant,
        rnCompliant,
        componentData.staffingLevels,
        componentData.turnoverRate
      );
      
      const qualityMeasuresScore = calculateQualityMeasuresScore(
        componentData.nqipIndicators,
        componentData.clinicalMetrics,
        componentData.safetyIndicators
      );
      
      // Create star rating object
      const starRating = createStarRating({
        facilityId,
        assessmentDate: today,
        effectiveDate: today,
        residentsExperience: {
          score: residentsExperienceScore,
          sources: {
            consumerSurvey: componentData.consumerSurvey || 0,
            familyFeedback: componentData.familyFeedback || 0,
            satisfactionMetrics: componentData.satisfactionMetrics || 0
          }
        },
        compliance: {
          score: complianceScore,
          sources: {
            qualityStandards: componentData.qualityStandards || 0,
            auditResults: componentData.auditResults || 0,
            sirsCompliance: componentData.sirsCompliance || 0
          },
          capsOverall: complianceScore <= 2
        },
        staffing: {
          score: staffingScore,
          sources: {
            careMinutesCompliance: careMinutesCompliant ? 5 : 0,
            rnMinutesCompliance: rnCompliant ? 5 : 0,
            staffingLevels: componentData.staffingLevels || 0,
            turnoverRate: componentData.turnoverRate || 0
          },
          meetsBothTargets: careMinutesCompliant && rnCompliant
        },
        qualityMeasures: {
          score: qualityMeasuresScore,
          sources: {
            nqipIndicators: componentData.nqipIndicators || 0,
            clinicalMetrics: componentData.clinicalMetrics || 0,
            safetyIndicators: componentData.safetyIndicators || 0
          }
        },
        benchmarks: {
          national: componentData.nationalBenchmark || 0,
          state: componentData.stateBenchmark || 0,
          peerGroup: componentData.peerBenchmark || 0
        }
      });
      
      // Calculate overall rating
      starRating.overallRating = calculateOverallRating(starRating);
      
      // Save
      const created = LocalStorageService.addItem(this.storageKey, starRating);
      return { success: true, data: created };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Auto-calculate star rating from current facility data
   */
  async autoCalculate(facilityId) {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Get care minutes compliance
      const careMinutesSummary = await CareMinutesService.getFacilityComplianceSummary(facilityId, today);
      
      // Get component data (in production, these would come from various sources)
      const componentData = {
        // Residents' Experience - would come from surveys/feedback
        consumerSurvey: 4.2, // Placeholder
        familyFeedback: 4.0, // Placeholder
        satisfactionMetrics: 4.1, // Placeholder
        
        // Compliance - would come from audits/quality standards
        qualityStandards: 4.5, // Placeholder
        auditResults: 4.3, // Placeholder
        sirsCompliance: 5.0, // Placeholder
        
        // Staffing - calculated from care minutes
        careMinutesCompliant: careMinutesSummary.success && 
          careMinutesSummary.data.complianceRate >= 95,
        rnCompliant: careMinutesSummary.success && 
          careMinutesSummary.data.rnCompliantResidents === careMinutesSummary.data.totalResidents,
        staffingLevels: 4.0, // Placeholder
        turnoverRate: 3.8, // Placeholder
        
        // Quality Measures - would come from NQIP/clinical data
        nqipIndicators: 4.2, // Placeholder
        clinicalMetrics: 4.0, // Placeholder
        safetyIndicators: 4.3 // Placeholder
      };
      
      return await this.calculateStarRating(facilityId, componentData);
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get impact analysis (what actions affect which component)
   */
  async getImpactAnalysis(facilityId) {
    try {
      const current = await this.getCurrentRating(facilityId);
      if (!current.success) {
        return { success: false, error: 'No current rating found' };
      }
      
      const rating = current.data;
      const impacts = [];
      
      // Analyze each component
      if (rating.compliance.score <= 2) {
        impacts.push({
          component: 'Compliance',
          issue: 'Low compliance score caps overall rating at 2 stars',
          priority: 'CRITICAL',
          recommendations: [
            'Improve Quality Standards compliance',
            'Address audit findings',
            'Ensure SIRS compliance'
          ]
        });
      }
      
      if (!rating.staffing.meetsBothTargets) {
        impacts.push({
          component: 'Staffing',
          issue: 'Not meeting both care minutes targets',
          priority: 'HIGH',
          recommendations: [
            'Ensure total care minutes compliance (215 min/day)',
            'Ensure RN minutes compliance (44 min/day)',
            'Review staffing levels and turnover'
          ]
        });
      }
      
      if (rating.residentsExperience.score < 4) {
        impacts.push({
          component: 'Residents\' Experience',
          issue: 'Below target experience score',
          priority: 'MEDIUM',
          recommendations: [
            'Improve consumer survey scores',
            'Enhance family feedback',
            'Address satisfaction concerns'
          ]
        });
      }
      
      if (rating.qualityMeasures.score < 4) {
        impacts.push({
          component: 'Quality Measures',
          issue: 'Below target quality metrics',
          priority: 'MEDIUM',
          recommendations: [
            'Improve NQIP indicators',
            'Enhance clinical metrics',
            'Address safety indicators'
          ]
        });
      }
      
      return { success: true, data: impacts };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new StarRatingsService();
